import { useCallback, useEffect, useRef } from "react";
import type { FlowStepDescriptor } from "@portal/app/_common/lib/flowStepTracking";
import { useFlowStepTracking } from "@portal/app/_components/hooks/useFlowStepTracking";
import { useFlowContext } from "./FlowContext";

/** Stable positions include steps skipped for a returning customer. */
export const PROGRAM_FLOW_STEPS = {
  selection: { stepNumber: 1, stepName: "school_match" },
  email: { stepNumber: 2, stepName: "email" },
  contact: { stepNumber: 3, stepName: "contact_info" },
  phone: { stepNumber: 4, stepName: "phone_verify" },
  submit: { stepNumber: 5, stepName: "final_submit" },
} as const satisfies Record<string, FlowStepDescriptor>;

export type ProgramFlowStepCompletion = () => boolean;

/**
 * Bind progress to an explicit successful action, never a screen/state effect.
 * Shared intake components also serve coaching/sign-in: only programFlow emits.
 * Each submission owns its completion callback; tracking never gates product requests.
 */
export function useProgramFlowStepAction(descriptor: FlowStepDescriptor) {
  const {
    flow,
    stepShown,
    data: { programId },
  } = useFlowContext();
  const track = useFlowStepTracking("explore", flow === "programFlow");
  const mounted = useRef(true);
  const scopeKey = JSON.stringify([flow, programId, stepShown]);
  const scope = useRef({ key: scopeKey });
  // A new visit invalidates prior actions, including navigation away and Back.
  if (scope.current.key !== scopeKey) scope.current = { key: scopeKey };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return useCallback((): ProgramFlowStepCompletion | undefined => {
    if (!mounted.current) return undefined;
    let completed = false;
    const actionScope = scope.current;
    return () => {
      if (completed || !mounted.current || scope.current !== actionScope) return false;
      completed = true;
      track(descriptor);
      return true;
    };
  }, [descriptor, track]);
}
